/**
 * Ghost Mode (X-ray) — toggle global semi-transparency for every loaded
 * model so the user can see through walls / floors / etc.
 *
 * Implementation:
 *   - FRAGS: highlight ALL elements in every FragmentsModel with a
 *     transparent MaterialDefinition (opacity 0.25). Currently-selected
 *     elements are re-highlighted afterwards so they stay obvious.
 *   - GLTF: traverse each viewer GLTF root, snapshot the original
 *     material flags (transparent / opacity / depthWrite), then force
 *     them to a transparent X-ray look. Restored verbatim on toggle off.
 *
 *  Disable restores both kinds and re-applies any selection / isolation
 *  state that was active before ghost mode was turned on.
 */

import * as THREE from "three";
import * as FRAGS from "@thatopen/fragments";

interface FragmentsLike {
  models: { list: Map<string, FRAGS.FragmentsModel> };
  update: (force?: boolean) => unknown;
}

interface SetupOpts {
  fragments: FragmentsLike;
  /** Live map of GLTF model roots keyed by modelId. */
  gltfRoots: Map<string, THREE.Object3D>;
  /**
   * Re-apply the user's selection highlight after we've blanket-applied
   * the ghost material. Called once at enable and once at disable.
   */
  reapplySelection?: () => Promise<void> | void;
  /** True if isolate mode is active and should be re-applied on disable. */
  isIsolated?: () => boolean;
  /** Re-apply isolate after we reset highlights on disable. */
  reapplyIsolate?: () => Promise<void> | void;
}

interface MaterialSnapshot {
  transparent: boolean;
  opacity: number;
  depthWrite: boolean;
  side: THREE.Side;
}

export interface GhostModeHandle {
  isActive: () => boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  toggle: () => Promise<boolean>;
}

const GHOST_OPACITY = 0.25;

export function createGhostMode(opts: SetupOpts): GhostModeHandle {
  const { fragments, gltfRoots, reapplySelection, isIsolated, reapplyIsolate } =
    opts;

  let active = false;
  // Keyed by material instance so we can restore exactly what we changed,
  // including in the shared-material case (one material referenced by many
  // meshes — we only snapshot once).
  const gltfBackup = new Map<THREE.Material, MaterialSnapshot>();

  const ghostMat: FRAGS.MaterialDefinition = {
    color: new THREE.Color(0xbfd9dc),
    renderedFaces: FRAGS.RenderedFaces.TWO,
    opacity: GHOST_OPACITY,
    transparent: true,
  };

  async function collectAllFragIds(
    m: FRAGS.FragmentsModel
  ): Promise<number[]> {
    const cats = await m.getCategories();
    if (!cats || cats.length === 0) return [];
    const map = await m.getItemsOfCategories(
      cats.map((c) => new RegExp(`^${c}$`))
    );
    const out: number[] = [];
    for (const arr of Object.values(map)) out.push(...arr);
    return out;
  }

  async function applyToFrags() {
    for (const [, m] of fragments.models.list) {
      try {
        const ids = await collectAllFragIds(m);
        if (ids.length > 0) {
          await m.highlight(ids, ghostMat);
        }
      } catch (err) {
        console.warn(`[ghost] highlight failed on model ${m.modelId}`, err);
      }
    }
  }

  async function clearFragHighlights() {
    for (const [, m] of fragments.models.list) {
      try {
        const ids = await collectAllFragIds(m);
        if (ids.length > 0) {
          await m.resetHighlight(ids);
        }
      } catch (err) {
        console.warn(`[ghost] resetHighlight failed on model ${m.modelId}`, err);
      }
    }
  }

  function applyToGltf() {
    gltfRoots.forEach((root) => {
      root.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (!(mesh as any).isMesh) return;
        const mats = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];
        mats.forEach((rawMat) => {
          if (!rawMat) return;
          const mat = rawMat as THREE.Material & {
            opacity: number;
            transparent: boolean;
            depthWrite: boolean;
            side: THREE.Side;
            needsUpdate: boolean;
          };
          if (!gltfBackup.has(mat)) {
            gltfBackup.set(mat, {
              transparent: mat.transparent,
              opacity: mat.opacity,
              depthWrite: mat.depthWrite,
              side: mat.side,
            });
          }
          mat.transparent = true;
          mat.opacity = GHOST_OPACITY;
          mat.depthWrite = false;
          mat.side = THREE.DoubleSide;
          mat.needsUpdate = true;
        });
      });
    });
  }

  function restoreGltf() {
    gltfBackup.forEach((snap, mat) => {
      const m = mat as THREE.Material & {
        opacity: number;
        transparent: boolean;
        depthWrite: boolean;
        side: THREE.Side;
        needsUpdate: boolean;
      };
      m.transparent = snap.transparent;
      m.opacity = snap.opacity;
      m.depthWrite = snap.depthWrite;
      m.side = snap.side;
      m.needsUpdate = true;
    });
    gltfBackup.clear();
  }

  async function enable() {
    if (active) return;
    console.log("👻 [Ghost] Enabling X-ray mode…");
    await applyToFrags();
    applyToGltf();
    // Re-highlight selection so it pops through the ghost layer.
    try {
      await reapplySelection?.();
    } catch (err) {
      console.warn("[ghost] reapplySelection failed", err);
    }
    try {
      fragments.update(true);
    } catch {
      /* noop */
    }
    active = true;
  }

  async function disable() {
    if (!active) return;
    console.log("👻 [Ghost] Disabling X-ray mode…");
    await clearFragHighlights();
    restoreGltf();
    // Re-establish isolate (which itself uses transparent material on
    // non-isolated elements) or selection highlight.
    try {
      if (isIsolated?.()) {
        await reapplyIsolate?.();
      } else {
        await reapplySelection?.();
      }
    } catch (err) {
      console.warn("[ghost] re-establish post-disable failed", err);
    }
    try {
      fragments.update(true);
    } catch {
      /* noop */
    }
    active = false;
  }

  async function toggle(): Promise<boolean> {
    if (active) {
      await disable();
    } else {
      await enable();
    }
    return active;
  }

  return {
    isActive: () => active,
    enable,
    disable,
    toggle,
  };
}
